import { AnimatePresence, motion } from 'framer-motion';
import { Newspaper } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { usePreviewImage } from '../../hooks/usePreviewImage';
import { useNews, type NewsItem } from '../../hooks/useWidgetsData';
import { useSettings } from '../../store/settings';
import { timeAgo, useArticle } from '../ArticleModal';

/**
 * One story at a time – picture, headline, summary – rotating every few seconds. compact uses
 * a letterbox picture for narrow columns. fill: the card has a fixed-size box (Custom layout);
 * when that box is wider than it is tall, the picture sits beside the text.
 */
export function NewsCard({ compact = false, fill = false }: { compact?: boolean; fill?: boolean }) {
  const interval = useSettings((s) => s.news.cardIntervalSec);
  const open = useArticle((s) => s.open);
  const { data, loading } = useNews();
  const [index, setIndex] = useState(0);
  const items = data ?? [];

  useEffect(() => {
    if (items.length < 2) return;
    const t = window.setInterval(() => setIndex((i) => (i + 1) % items.length), interval * 1000);
    return () => window.clearInterval(t);
  }, [items.length, interval]);

  if (!items.length) {
    return <div className="glass muted p-4 text-sm">{loading ? 'Loading news…' : 'No news right now.'}</div>;
  }
  const item = items[index % items.length]!;
  return <Card item={item} index={index} total={items.length} interval={interval} compact={compact} fill={fill} onOpen={() => open(item)} />;
}

function Card({
  item,
  index,
  total,
  interval,
  compact,
  fill,
  onOpen,
}: {
  item: NewsItem;
  index: number;
  total: number;
  interval: number;
  compact: boolean;
  fill: boolean;
  onOpen: () => void;
}) {
  const image = usePreviewImage(item.image, item.link);
  const side = useSettings((s) => s.news.imageSide);

  // In a fixed box, follow its shape: wider than tall → picture beside the text.
  const box = useRef<HTMLButtonElement>(null);
  const [wide, setWide] = useState(false);
  useEffect(() => {
    const el = box.current;
    if (!fill || !el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setWide(el.clientWidth > el.clientHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [fill]);
  const beside = fill && wide;

  const imageClass = beside
    ? 'h-full w-2/5 shrink-0 object-cover'
    : fill
      ? // Taller box: picture on top with a guaranteed half of the height, story underneath.
        'min-h-0 w-full shrink-0 basis-1/2 object-cover'
      : `w-full object-cover ${compact ? 'aspect-[21/9]' : 'aspect-[16/9]'}`;

  return (
    <button
      ref={box}
      onClick={onOpen}
      className={`glass w-full overflow-hidden text-left transition hover:brightness-110 ${fill ? 'flex h-full flex-col' : 'block'}`}
      aria-label={`Open story: ${item.title}`}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={`${index}-${item.link ?? item.title}`}
          className={fill ? `flex min-h-0 flex-1 ${beside ? (side === 'right' ? 'flex-row-reverse' : 'flex-row') : 'flex-col'}` : undefined}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
        >
          {image && <img src={image} alt="" className={imageClass} loading="lazy" onError={(e) => (e.currentTarget.style.display = 'none')} />}
          <div className={`space-y-1.5 p-4 ${beside ? 'min-w-0 flex-1 overflow-hidden' : fill ? 'min-h-0 flex-1 overflow-hidden' : ''}`}>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[var(--accent)]">
              <Newspaper className="h-3.5 w-3.5" aria-hidden /> {item.source}
              <span className="muted font-normal normal-case">{timeAgo(item.published)}</span>
            </div>
            <div className="font-heading text-lg font-medium leading-snug">{item.title}</div>
            {item.summary && <p className={`muted text-sm ${beside ? 'line-clamp-6' : 'line-clamp-3'}`}>{item.summary}</p>}
          </div>
        </motion.div>
      </AnimatePresence>
      {total > 1 && (
        <div className="h-0.5 bg-[var(--border)]">
          <motion.div
            key={index}
            className="h-full bg-[var(--accent)]"
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: interval, ease: 'linear' }}
          />
        </div>
      )}
    </button>
  );
}
